/**
 * Shared empty-state banner — dashed-border card with a coloured icon
 * chip, a heading, and a one-liner body. Drop-in replacement for the
 * bare `<p>No X found.</p>` patterns scattered across pages.
 *
 *   <EmptyStateBanner
 *     icon={Users}
 *     title="No members yet"
 *     body="Invite teammates to populate this list."
 *   />
 *
 * Props
 *   icon    — lucide icon component (REQUIRED)
 *   title   — short heading (REQUIRED, ~3–6 words)
 *   body    — supporting copy (optional, ~1 sentence)
 *   tone    — colour palette for the icon chip. Options:
 *               'sky'     (default) — calm informational
 *               'amber'   — attention / try-something prompt
 *               'emerald' — success / "all caught up"
 *               'rose'    — empty-because-error / "nothing to act on"
 *               'violet'  — generic "no data yet"
 *   compact — when true, drops the vertical padding so the banner fits
 *             inside a small chart card without dwarfing it. Default
 *             `false`.
 *
 * The banner is purely presentational — no state, no effects, no API
 * calls. Pages that need an action button (e.g. "Connect SOP Galaxy")
 * still render their own bespoke empty state because the banner stays
 * minimal on purpose.
 */
export function EmptyStateBanner({ icon: Icon, title, body, tone = 'sky', compact = false }) {
  const palette = TONES[tone] ?? TONES.sky
  const pad = compact ? 'py-6' : 'py-12'
  return (
    <div
      className={`rounded-xl border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] px-6 ${pad} text-center`}
    >
      <div
        className="inline-flex items-center justify-center h-12 w-12 rounded-full mb-3"
        style={{
          backgroundColor: palette.bg,
          color: palette.fg,
          border: `1px solid ${palette.border}`,
        }}
      >
        <Icon size={20} />
      </div>
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-1">
        {title}
      </h2>
      {body ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto">
          {body}
        </p>
      ) : null}
    </div>
  )
}

// Tonal palettes — chosen to match the brand's existing chip colours
// across the app (Files & Links uses sky for "no files", Performance
// uses violet for empty stats, etc.). Hex values match Tailwind's
// `*-50 / *-700 / *-200` shades for consistency.
const TONES = {
  sky:     { bg: '#E0F2FE', fg: '#0369A1', border: '#BAE6FD' },
  amber:   { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  emerald: { bg: '#D1FAE5', fg: '#059669', border: '#A7F3D0' },
  rose:    { bg: '#FFE4E6', fg: '#BE123C', border: '#FECDD3' },
  violet:  { bg: '#EDE9FE', fg: '#6D28D9', border: '#DDD6FE' },
}
